// DEV-7943: Orders Page Redux Slice - Backend Integration

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	type RestockSessionDetailsFragment,
	type RestockSessionOrderItemDocFragment
} from "~/gql/queries/restock/restockSession.generated";
import applyOptimisticOverride, { type OverrideAction } from "~restock/helpers/applyOptimisticOverride";
import computeMetrics from "~restock/helpers/computeMetrics";

/** Order items in Redux are populated exclusively by the paginated document query
 *  (GetRestockSessionOrderItems) — the session fragment no longer selects orderItems. */
export type OrderItemRow = RestockSessionOrderItemDocFragment;

// UI-specific types that are not from the backend
export type ReserveLevel = "category" | "item" | "productGroup" | "program" | "subCategory";
export type ReserveMode = "allocate" | "reserve";

export interface ReserveRule {
	id: string;
	level: ReserveLevel;
	mode?: ReserveMode;
	name: string;
	percentage: number;
	value: string;
}

export interface Metrics {
	allocationPercent: number;
	costOfAllocated: number;
	costOfAvailable: number;
	totalAvailable: number;
	totalCarts: number;
	totalInTransit: number;
	totalRemaining: number;
	totalRemainingValue: number;
}

export type MetricsUom = "carriers" | "eaches" | "units";
export type MetricsValueBasis = "cogs" | "retail" | "wholesale";

const DEFAULT_METRICS_UOM: MetricsUom = "units";
const DEFAULT_METRICS_VALUE_BASIS: MetricsValueBasis = "wholesale";

export type MetricsFilterTokenKind = "category" | "item" | "itemGroup" | "productGroup" | "program" | "subcategory";

export interface MetricsFilterToken {
	id: string;
	itemNumber?: string;
	kind: MetricsFilterTokenKind;
	value: string;
}

const toFilterTokenKey = (token: Pick<MetricsFilterToken, "id" | "kind">): string => `${token.kind}|${token.id}`;

export type GroupByOption = "category" | "item" | "productGroup" | "program" | "subCategory";

export type StoreGroupByOption = "customer" | "deliveryDay" | "none" | "rank" | "zone";

export interface BatchProgress {
	currentBatch: number;
	error: string | null;
	phase: "calculating" | "idle" | "persisting" | "populating";
	totalBatches: number;
}

export type SessionCreationStep = "items" | "name" | "overrides" | "stores" | null;

export interface PendingCreationSession {
	sessionId: string;
	startedAtMs: number;
}

/** Scope captured for the current session: which items/locations are in-play and overrides for calc. */
export interface SessionScope {
	itemIds: string[];
	locationIds: string[];
	targetWeeksOfSupply: number;
}

/** Session state carries the codegen session fragment plus orderItems populated
 *  separately from the paginated GetRestockSessionOrderItems query. */
interface RestockSessionState extends RestockSessionDetailsFragment {
	orderItems: OrderItemRow[];
}

interface RestockOrdersState {
	batchProgress: BatchProgress;
	currentSession: RestockSessionState | null;

	restockStationRoutes: string[];
	restockUserStation: string | null;
	restockUserStations: string[];

	filterSearch: string;
	filterTokens: MetricsFilterToken[];
	focusedItemId: string | null;
	focusOnUnit: boolean;
	groupBy: GroupByOption;

	/** True while the paginated GetRestockSessionOrderItems query is still fetching pages for the current session. */
	isFetchingOrderItems: boolean;

	lastSyncedAt?: string;

	metrics: Metrics;
	metricsUom: MetricsUom;
	metricsValueBasis: MetricsValueBasis;

	newSessionName: string;
	pendingCreationSessions: PendingCreationSession[];
	pendingTargetWeeksOfSupply: number | null;

	reserveAllocateOpen: boolean;

	reserveMode: ReserveMode;
	reserveRules: ReserveRule[];
	roundToCartOverrideOpen: boolean;

	selectedFilterNodes: string[];
	selectedRowIds: string[];

	createSessionDialogOpen: boolean;
	sessionCreationStep: SessionCreationStep;
	sessionManagerOpen: boolean;
	/** Captured scope of the current session (used to fire calculation when session changes). */
	sessionScope: SessionScope | null;
	stationSelectionDialogOpen: boolean;
	storeGroupBy: StoreGroupByOption;

	submitOrdersOpen: boolean;

	wosOverrideOpen: boolean;
}

const initialMetrics: Metrics = {
	allocationPercent: 0,
	costOfAllocated: 0,
	costOfAvailable: 0,
	totalAvailable: 0,
	totalCarts: 0,
	totalInTransit: 0,
	totalRemaining: 0,
	totalRemainingValue: 0
};

export const initialState: RestockOrdersState = {
	batchProgress: { currentBatch: 0, error: null, phase: "idle", totalBatches: 0 },
	currentSession: null,

	restockStationRoutes: [],
	restockUserStation: null,
	restockUserStations: [],

	filterSearch: "",
	filterTokens: [],
	focusedItemId: null,
	focusOnUnit: true,
	groupBy: "category",

	isFetchingOrderItems: false,

	lastSyncedAt: undefined,

	metrics: initialMetrics,
	metricsUom: DEFAULT_METRICS_UOM,
	metricsValueBasis: DEFAULT_METRICS_VALUE_BASIS,

	newSessionName: "",
	pendingCreationSessions: [],
	pendingTargetWeeksOfSupply: null,

	reserveAllocateOpen: false,

	reserveMode: "reserve",
	reserveRules: [],
	roundToCartOverrideOpen: false,

	selectedFilterNodes: [],
	selectedRowIds: [],

	createSessionDialogOpen: false,
	sessionCreationStep: null,
	sessionManagerOpen: false,
	sessionScope: null,
	stationSelectionDialogOpen: false,
	storeGroupBy: "none",

	submitOrdersOpen: false,

	wosOverrideOpen: false
};

export const restockOrderSlice = createSlice({
	initialState,
	name: "restockOrder",
	reducers: {
		addFilterToken: (state, action: PayloadAction<MetricsFilterToken>) => {
			const incomingKey = toFilterTokenKey(action.payload);
			if (state.filterTokens.some((t) => toFilterTokenKey(t) === incomingKey)) return;
			state.filterTokens.push(action.payload);
		},
		addPendingCreationSession: (state, action: PayloadAction<PendingCreationSession>) => {
			if (!state.pendingCreationSessions.some((s) => s.sessionId === action.payload.sessionId)) {
				state.pendingCreationSessions.push(action.payload);
			}
		},
		addReserveRule: (state, action: PayloadAction<ReserveRule>) => {
			state.reserveRules.push(action.payload);
		},
		applyBulkOptimisticOverride: (
			state,
			action: PayloadAction<{
				action: OverrideAction;
				itemKeys: { customerLocationId: string; itemId: string }[];
				quantity?: number;
			}>
		) => {
			if (!state.currentSession) return;
			const keySet = new Set(action.payload.itemKeys.map((k) => `${k.itemId}|${k.customerLocationId}`));
			for (const item of state.currentSession.orderItems) {
				if (keySet.has(`${item.itemId}|${item.customerLocationId}`)) {
					applyOptimisticOverride(item, action.payload.action, action.payload.quantity);
				}
			}
			state.metrics = computeMetrics(state.currentSession.orderItems);
		},
		clearFilters: (state) => {
			state.filterSearch = "";
			state.selectedFilterNodes = [];
		},
		clearFilterTokens: (state) => {
			state.filterTokens = [];
		},
		clearNewSessionName: (state) => {
			state.newSessionName = "";
		},
		clearSelectedRowIds: (state) => {
			state.selectedRowIds = [];
		},
		clearSessionCreationFlow: (state) => {
			state.sessionCreationStep = null;
			state.newSessionName = "";
			state.pendingTargetWeeksOfSupply = null;
		},
		closeCreateSessionDialog: (state) => {
			state.createSessionDialogOpen = false;
			state.sessionCreationStep = null;
			state.newSessionName = "";
			state.pendingTargetWeeksOfSupply = null;
		},
		closeReserveAllocate: (state) => {
			state.reserveAllocateOpen = false;
		},
		closeSessionManager: (state) => {
			state.sessionManagerOpen = false;
		},
		closeStationSelectionDialog: (state) => {
			state.stationSelectionDialogOpen = false;
		},
		closeSubmitOrders: (state) => {
			state.submitOrdersOpen = false;
		},
		closeWosOverride: (state) => {
			state.wosOverrideOpen = false;
		},
		openCreateSessionDialog: (state) => {
			state.createSessionDialogOpen = true;
			state.sessionCreationStep = "stores";
			state.pendingTargetWeeksOfSupply = null;
		},
		openReserveAllocate: (state) => {
			state.reserveAllocateOpen = true;
		},
		openSessionManager: (state) => {
			state.sessionManagerOpen = true;
		},
		openStationSelectionDialog: (state) => {
			state.stationSelectionDialogOpen = true;
		},
		openSubmitOrders: (state) => {
			state.submitOrdersOpen = true;
		},
		openWosOverride: (state) => {
			state.wosOverrideOpen = true;
		},
		removeFilterToken: (state, action: PayloadAction<Pick<MetricsFilterToken, "id" | "kind">>) => {
			const targetKey = toFilterTokenKey(action.payload);
			state.filterTokens = state.filterTokens.filter((t) => toFilterTokenKey(t) !== targetKey);
		},
		removePendingCreationSession: (state, action: PayloadAction<string>) => {
			state.pendingCreationSessions = state.pendingCreationSessions.filter((s) => s.sessionId !== action.payload);
		},
		removeReserveRule: (state, action: PayloadAction<string>) => {
			state.reserveRules = state.reserveRules.filter((rule) => rule.id !== action.payload);
		},
		resetOrdersState: () => initialState,
		setBatchProgress: (state, action: PayloadAction<BatchProgress>) => {
			state.batchProgress = action.payload;
		},
		setRestockStationRoutes: (state, action: PayloadAction<string[]>) => {
			state.restockStationRoutes = action.payload;
		},
		setRestockUserStation: (state, action: PayloadAction<string | null>) => {
			state.restockUserStation = action.payload;
		},
		setRestockUserStations: (state, action: PayloadAction<string[]>) => {
			state.restockUserStations = action.payload;
		},
		setCurrentSession: (state, action: PayloadAction<RestockSessionDetailsFragment | null>) => {
			state.currentSession = action.payload ? { ...action.payload, orderItems: [] } : null;
			state.filterTokens = [];
			state.metrics = initialMetrics;
			state.metricsUom = DEFAULT_METRICS_UOM;
			state.metricsValueBasis = DEFAULT_METRICS_VALUE_BASIS;
			if (action.payload?.updatedAt) {
				state.lastSyncedAt = action.payload.updatedAt;
			}
			if (!action.payload) {
				state.sessionScope = null;
			}
		},
		updateCurrentSession: (state, action: PayloadAction<Partial<RestockSessionDetailsFragment>>) => {
			if (state.currentSession) {
				state.currentSession = { ...state.currentSession, ...action.payload };
			}
		},
		setFilterSearch: (state, action: PayloadAction<string>) => {
			state.filterSearch = action.payload;
		},
		setFocusedItemId: (state, action: PayloadAction<null | string>) => {
			state.focusedItemId = action.payload;
		},
		setGroupBy: (state, action: PayloadAction<GroupByOption>) => {
			state.groupBy = action.payload;
		},
		setIsFetchingOrderItems: (state, action: PayloadAction<boolean>) => {
			state.isFetchingOrderItems = action.payload;
		},
		setLastSyncedAt: (state, action: PayloadAction<string>) => {
			state.lastSyncedAt = action.payload;
		},
		setMetricsUom: (state, action: PayloadAction<MetricsUom>) => {
			state.metricsUom = action.payload;
		},
		setMetricsValueBasis: (state, action: PayloadAction<MetricsValueBasis>) => {
			state.metricsValueBasis = action.payload;
		},
		setNewSessionName: (state, action: PayloadAction<string>) => {
			state.newSessionName = action.payload;
		},
		setOrderItems: (state, action: PayloadAction<OrderItemRow[]>) => {
			if (!state.currentSession) return;
			state.currentSession.orderItems = action.payload;
			state.metrics = computeMetrics(state.currentSession.orderItems);
		},
		setPendingTargetWeeksOfSupply: (state, action: PayloadAction<number | null>) => {
			state.pendingTargetWeeksOfSupply = action.payload;
		},
		setReserveMode: (state, action: PayloadAction<ReserveMode>) => {
			state.reserveMode = action.payload;
		},
		setReserveRules: (state, action: PayloadAction<ReserveRule[]>) => {
			state.reserveRules = action.payload;
		},
		closeRoundToCartOverride: (state) => {
			state.roundToCartOverrideOpen = false;
		},
		openRoundToCartOverride: (state) => {
			state.roundToCartOverrideOpen = true;
		},
		setSelectedFilterNodes: (state, action: PayloadAction<string[]>) => {
			state.selectedFilterNodes = action.payload;
		},
		setSelectedRowIds: (state, action: PayloadAction<string[]>) => {
			state.selectedRowIds = action.payload;
		},
		setSessionCreationStep: (state, action: PayloadAction<SessionCreationStep>) => {
			state.sessionCreationStep = action.payload;
		},
		setSessionScope: (state, action: PayloadAction<SessionScope | null>) => {
			state.sessionScope = action.payload;
		},
		setStoreGroupBy: (state, action: PayloadAction<StoreGroupByOption>) => {
			state.storeGroupBy = action.payload;
		},
		toggleFocusOnUnit: (state) => {
			state.focusOnUnit = !state.focusOnUnit;
		},
		updateReserveRule: (state, action: PayloadAction<ReserveRule>) => {
			const index = state.reserveRules.findIndex((rule) => rule.id === action.payload.id);
			if (index >= 0) {
				state.reserveRules[index] = action.payload;
			}
		}
	}
});

export const {
	addFilterToken,
	addPendingCreationSession,
	addReserveRule,
	applyBulkOptimisticOverride,
	clearFilters,
	clearFilterTokens,
	clearNewSessionName,
	clearSelectedRowIds,
	clearSessionCreationFlow,
	closeCreateSessionDialog,
	closeReserveAllocate,
	closeRoundToCartOverride,
	closeSessionManager,
	closeStationSelectionDialog,
	closeSubmitOrders,
	closeWosOverride,
	openCreateSessionDialog,
	openReserveAllocate,
	openRoundToCartOverride,
	openSessionManager,
	openStationSelectionDialog,
	openSubmitOrders,
	openWosOverride,
	removeFilterToken,
	removePendingCreationSession,
	removeReserveRule,
	resetOrdersState,
	setBatchProgress,
	setCurrentSession,
	setRestockStationRoutes,
	setRestockUserStation,
	setRestockUserStations,
	setFilterSearch,
	setFocusedItemId,
	setGroupBy,
	setIsFetchingOrderItems,
	setLastSyncedAt,
	setMetricsUom,
	setMetricsValueBasis,
	setNewSessionName,
	setOrderItems,
	setPendingTargetWeeksOfSupply,
	setReserveMode,
	setReserveRules,
	setSelectedFilterNodes,
	setSelectedRowIds,
	setSessionCreationStep,
	setSessionScope,
	setStoreGroupBy,
	toggleFocusOnUnit,
	updateCurrentSession,
	updateReserveRule
} = restockOrderSlice.actions;

export const selectOrderItemDocs = (state: {
	restockOrder: RestockOrdersState;
}): RestockSessionOrderItemDocFragment[] => state.restockOrder.currentSession?.orderItems ?? [];

export interface DistinctItemSummary {
	available: number;
	itemId: string;
	itemName: string;
	storeCount: number;
	totalDemand: number;
}

const EMPTY_DISTINCT_ITEMS: DistinctItemSummary[] = [];

/** Distinct items in the active session, one entry per itemId, ordered by total
 *  demand (desc). `totalDemand` sums `demandQuantity` (per-store calculated demand), not
 *  `orderQuantity` (the committed order size, which shifts with allocations/overrides and
 *  would make the sort key unstable as the user works). `available` is ERP-per-item
 *  (same on every store row for a given itemId), so the first seen row's value is
 *  authoritative. Memoized so the returned array reference is stable across unrelated
 *  Redux state changes. */
export const selectDistinctItems = createSelector(
	[(state: { restockOrder: RestockOrdersState }) => state.restockOrder.currentSession?.orderItems],
	(rows): DistinctItemSummary[] => {
		if (!rows || rows.length === 0) return EMPTY_DISTINCT_ITEMS;

		const byItem = new Map<string, DistinctItemSummary>();
		for (const row of rows) {
			const existing = byItem.get(row.itemId);
			if (existing) {
				existing.storeCount += 1;
				existing.totalDemand += row.demandQuantity ?? 0;
				continue;
			}
			byItem.set(row.itemId, {
				available: row.available ?? 0,
				itemId: row.itemId,
				itemName: row.itemName,
				storeCount: 1,
				totalDemand: row.demandQuantity ?? 0
			});
		}
		return [...byItem.values()].sort((a, b) => b.totalDemand - a.totalDemand);
	}
);

/** ERP availability for a given itemId, derived from any store row in the current
 *  session. Returns 0 when the item is not present or no session is loaded. */
export const selectAvailableInventoryByItem = (state: { restockOrder: RestockOrdersState }, itemId: string): number => {
	const rows = state.restockOrder.currentSession?.orderItems;
	if (!rows) return 0;
	const match = rows.find((r) => r.itemId === itemId);
	return match?.available ?? 0;
};

export default restockOrderSlice.reducer;
